import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  Icon,
  Badge,
  Flex,
  useColorMode,
} from "@chakra-ui/react";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import { FaHandshake, FaChartLine } from "react-icons/fa";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor } from "../../brandConfig";

const partnerModules = [
  {
    name: "Accountability (Wheel of Life)",
    description:
      "Holistic life coaching with radar charts for life balance assessment, goal tracking, and action management. Built for Blooming Brilliant's coaching practice.",
    partner: "Blooming Brilliant",
    partnerUrl: "https://bloomingbrilliant.com.au",
    icon: FaChartLine,
    color: "#9333EA",
    tags: ["Coaching", "Life Balance", "Goal Tracking"],
  },
];

const PartnersDirectory = () => {
  const { colorMode } = useColorMode();

  const bg = getColor("background.main", colorMode);
  const cardGradientBg =
    colorMode === "light"
      ? `linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 248, 248, 0.9) 50%, rgba(255, 255, 255, 0.95) 100%)`
      : `linear-gradient(135deg, rgba(20, 20, 20, 0.8) 0%, rgba(30, 30, 30, 0.6) 50%, rgba(20, 20, 20, 0.8) 100%)`;
  const cardBorder =
    colorMode === "light" ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)";
  const textPrimary = getColor(
    colorMode === "light" ? "text.primary" : "text.primaryDark",
    colorMode
  );
  const textSecondary = getColor(
    colorMode === "light" ? "text.secondary" : "text.secondaryDark",
    colorMode
  );

  return (
    <Box bg={bg} minHeight="100vh">
      <NavbarWithCallToAction />

      <Container maxW="container.lg" py={16}>
        <VStack spacing={10} align="stretch">
          <Box textAlign="center">
            <Icon as={FaHandshake} w={12} h={12} color="#9333EA" mb={4} />
            <Heading as="h1" size="xl" color={textPrimary} mb={3}>
              Partner Modules
            </Heading>
            <Text fontSize="lg" color={textSecondary} maxW="600px" mx="auto">
              Specialized modules built for our community partners. Each module
              is tailored to a specific business need and hosted on the
              partner's own tenant site.
            </Text>
          </Box>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {partnerModules.map((module) => (
              <Card
                key={module.name}
                bg={cardGradientBg}
                backdropFilter="blur(10px)"
                boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
                borderRadius="xl"
                borderWidth="1px"
                borderColor={cardBorder}
                overflow="hidden"
                transition="all 0.3s ease"
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow: `0 12px 40px 0 ${module.color}30`,
                  borderColor: `${module.color}60`,
                }}
              >
                <CardHeader
                  bg="transparent"
                  py={4}
                  borderBottomWidth="1px"
                  borderColor={cardBorder}
                >
                  <Flex align="center">
                    <Icon
                      as={module.icon}
                      w={6}
                      h={6}
                      color={module.color}
                      mr={3}
                    />
                    <Box>
                      <Heading size="md" color={textPrimary}>
                        {module.name}
                      </Heading>
                      <Text fontSize="sm" color={textSecondary}>
                        by {module.partner}
                      </Text>
                    </Box>
                  </Flex>
                </CardHeader>
                <CardBody p={5}>
                  <Text fontSize="sm" color={textSecondary} mb={4}>
                    {module.description}
                  </Text>
                  <Flex gap={2} flexWrap="wrap">
                    {module.tags.map((tag) => (
                      <Badge
                        key={tag}
                        colorScheme="purple"
                        variant="subtle"
                        fontSize="xs"
                      >
                        {tag}
                      </Badge>
                    ))}
                  </Flex>
                </CardBody>
                <CardFooter
                  bg="rgba(0, 0, 0, 0.1)"
                  py={3}
                  borderTopWidth="1px"
                  borderColor={cardBorder}
                >
                  <Button
                    size="sm"
                    width="100%"
                    colorScheme="purple"
                    rightIcon={<ExternalLinkIcon />}
                    as="a"
                    href={module.partnerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Visit {module.partner}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </SimpleGrid>

          <Box
            textAlign="center"
            p={8}
            bg={cardGradientBg}
            borderRadius="xl"
            borderWidth="1px"
            borderColor={cardBorder}
          >
            <Heading size="md" color={textPrimary} mb={3}>
              Become a Partner
            </Heading>
            <Text fontSize="sm" color={textSecondary} mb={4}>
              Have a specialized module idea? We build custom solutions for
              community partners with their own branded tenant site.
            </Text>
            <Button
              colorScheme="purple"
              variant="outline"
              as="a"
              href="/live-event/interest"
            >
              Express Interest
            </Button>
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default PartnersDirectory;
